// English versions of the chapters.
import { STOPS } from './stops.js';

// English versions of the stops, same order (tags and places are shared)
const STOPS_EN = [
  {
    k: 'Hello',
    title: "I'm Javier Castellano",
    role: 'Product & Engineering Lead · Barcelona · 11+ years building software',
    lead: 'I started as a full-stack developer and today I work at the intersection of product discovery, technical strategy and AI-enabled delivery.',
    sections: [
      [
        'What I do',
        [
          'I partner with internal stakeholders to understand business challenges, spot opportunities, prototype solutions and turn early ideas into clearly scoped, scalable products.',
          'I lead a team of five developers: architecture, planning, prioritization, code reviews, delivery and everyone’s professional growth.',
          'I use AI agents and workflow automation across discovery, prototyping, development, testing, documentation and operations, to cut repetitive work and focus on what has impact.',
        ],
      ],
      [
        'Where I’ve worked',
        [
          'The full lifecycle: from ideation and system design to implementation, deployment and continuous improvement.',
          'Scalable enterprise platforms, complex operational workflows, integrations, data processing and asynchronous architectures.',
        ],
      ],
      [
        'How I work',
        [
          'I’m driven by turning ambiguity into clarity and ideas into valuable products.',
          'Thoughtful technical decisions, strong communication and continuous learning.',
          'Leadership based on trust, mentorship and shared ownership.',
        ],
      ],
    ],
  },
  {
    k: '2015 – 2017',
    title: 'Aradoxa Solutions',
    role: 'Software Developer · Barcelona · on-site · 2 years 3 months',
    lead: 'My first home. A dual role: improving the company’s enterprise software and pioneering mobile, for food & beverage clients such as Nutrexpa and Gallina Blanca.',
    sections: [
      [
        'ERP',
        [
          'Designed and built new modules for a critical ERP and enhanced existing ones for operations and data management.',
          'Rich desktop UI in WPF with Telerik, WCF services and SQL Server, with TFS for version control.',
        ],
      ],
      [
        'Logistics app',
        [
          'Led the development of a brand-new mobile and tablet app in Xamarin.',
          'Transporters got real-time access to inventory and deliveries, making goods management much more efficient.',
          'Offline data in SQLite, UI in HTML5, CSS3 and jQuery.',
        ],
      ],
      [
        'Also',
        [
          'Took part in architecture decisions, bug fixing and performance work so both ERP and app stayed reliable and scalable.',
        ],
      ],
    ],
  },
  {
    k: '2017 – 2021',
    title: 'Welocalize',
    role: 'Software Developer · Barcelona · 4 years 4 months',
    lead: 'I joined as a full-stack developer in a team building and scaling the mission-critical platforms of one of the world’s largest localization companies.',
    sections: [
      [
        'Localization ERP',
        [
          'A project localization ERP built from scratch as a team: Vue on the front end, Node on the back end, hosted on AWS.',
          'Integrated Elasticsearch to optimize vendor search and task assignment, a key feature for the business.',
        ],
      ],
      [
        'Automation',
        [
          'Contributed to a microservices platform (Lambda and Kinesis) with Bull/Redis queues that automated manual tasks with significant cost savings.',
        ],
      ],
      [
        'WIPO patents',
        [
          'A large-scale data system to process and manage millions of patents from the World Intellectual Property Organization.',
          'MongoDB for 2M+ records, with seamless access for clients.',
        ],
      ],
    ],
  },
  {
    k: '2021 – 2023',
    title: 'Software Lead',
    role: 'Welocalize · Barcelona · team of 6 · 2 years',
    lead: 'Promoted to Software Lead: the move from hands-on contributor to a leader focused on empowering the team and shaping a key product from the ground up.',
    sections: [
      [
        'The product',
        [
          'A vendor management worksurface covering the whole vendor lifecycle: sourcing, flexible onboarding, skill validation and job matching.',
          'A tailored experience for every vendor and a marketplace for new job opportunities.',
        ],
      ],
      [
        'Leadership',
        [
          'Led, mentored and coached 6 developers in an environment of technical excellence and continuous growth.',
          'Sprint planning, backlog management and regular code reviews for high-quality, scalable solutions.',
        ],
      ],
      [
        'Architecture & delivery',
        [
          'Key architectural decisions and integrations with third-party certification and training platforms.',
          'Orchestrated automation agents to improve efficiency.',
          'End-to-end delivery, working closely with product managers and stakeholders.',
        ],
      ],
    ],
  },
  {
    k: '2023 – 2026',
    title: 'Software Lead · Welodata',
    role: 'Welocalize · remote · team of 6 · 2 years 6 months',
    lead: 'Welodata is the business unit that builds high-quality datasets to train AI models. There we built a mission-critical platform from the ground up.',
    sections: [
      [
        'From trackers to systems',
        [
          'Everything used to live in trackers. Now there are three platforms:',
          '<b>Opportunities:</b> operations configure the work and categorize people by which clients they are eligible for.',
          '<b>Backoffice:</b> management of opportunities and users.',
          '<b>Operations:</b> an ERP for massive data-labeling and microtask workflows: project tracking, worker payments, access to the third-party platforms where they feed clients’ AIs, and automated client billing, whether the work happens on our platform or elsewhere.',
        ],
      ],
      [
        'Team',
        [
          'Led and mentored 6 engineers, with a culture of continuous learning and high performance.',
          'Worked with other teams on the project, sharing technical guidance and best practices for a cohesive final product.',
        ],
      ],
    ],
  },
  {
    k: '2026 – now',
    title: 'Product & Engineering Lead',
    role: 'Welocalize · Barcelona · remote · team of 5',
    lead: 'Product strategy, engineering leadership and AI-enabled delivery, turning complex operational challenges into scalable software.',
    sections: [
      [
        'Product',
        [
          'I work with internal stakeholders to understand needs, define requirements, prototype and scope new developments, from idea to delivery.',
          'I prototype concepts and workflows to validate quickly, align people and reduce uncertainty before building at scale.',
          'I define technical scope, MVPs, delivery plans and architectural approaches for new platform capabilities.',
        ],
      ],
      [
        'Engineering',
        [
          'Internal platforms for data-labeling, microtasks, workforce, projects, pipelines and operations.',
          'Backend, frontend, databases, integrations, asynchronous processing and automation.',
          'Technical direction, mentorship and feedback for five developers across the whole lifecycle.',
        ],
      ],
      [
        'AI every day',
        [
          'AI agents and workflow automation across discovery, prototyping, implementation, testing, documentation and releases, so the team can focus on higher-value problems.',
          'Like the Sagrada Família: built with patience, piece by piece, always looking up.',
        ],
      ],
    ],
  },
  {
    k: 'Stack & contact',
    title: 'Let’s talk',
    role: 'What I use every day',
    lead: 'I build both the systems and the teams needed to turn ambiguity into reliable, scalable, valuable products.',
    sections: [
      ['Backend', ['Go (Gin, GORM), REST APIs, event-driven systems, River Queue and Kinesis.']],
      ['Frontend', ['Vue 3, TypeScript and Module Federation.']],
      ['Data & infrastructure', ['PostgreSQL, MongoDB, Redis, AWS and OpenTelemetry.']],
      [
        'Contact',
        [
          '<a href="https://www.linkedin.com/in/javier-castellano-pozo-7767a4163/" target="_blank" rel="noopener">LinkedIn · Javier Castellano Pozo</a>',
        ],
      ],
    ],
  },
];
STOPS.forEach((st, i) => (st.en = STOPS_EN[i]));
